import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "destructive";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const base =
  "inline-flex items-center justify-center gap-2 rounded-md font-medium " +
  "transition-colors duration-fast ease-standard " +
  "disabled:opacity-50 disabled:cursor-not-allowed " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2";

const variants: Record<Variant, string> = {
  // §v3.makeover (2026-06-13) — was the legacy silver `brand-600`; now the
  // canonical primary: the magenta→coral accent gradient with dark ink, to
  // match `.xp-btn.primary` (xpand-pages.css) everywhere this button is used
  // (Training, Users, Roles, ConversationDetail).
  primary:
    "bg-[image:var(--accent-grad)] text-[#1b0a18] font-semibold border border-white/40 shadow-sm hover:shadow-md hover:brightness-105",
  secondary:
    "bg-surface-raised text-text-primary border border-border hover:bg-surface-sunken",
  ghost:
    "bg-transparent text-text-primary hover:bg-surface-sunken",
  destructive:
    "bg-danger-bg text-danger-fg border border-danger-border hover:brightness-95",
};

const sizes: Record<Size, string> = {
  sm: "h-9 px-3 text-sm min-w-9",
  md: "h-touch px-4 text-base min-w-touch",
  lg: "h-11 px-5 text-lg",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      className,
      loading,
      disabled,
      children,
      type = "button",
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      aria-busy={loading || undefined}
      disabled={loading || disabled}
      className={cn(base, variants[variant], sizes[size], className)}
      {...rest}
    >
      {loading ? (
        <span
          aria-hidden="true"
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
        />
      ) : null}
      {children}
    </button>
  ),
);
Button.displayName = "Button";
